import { useState } from 'react'
import { ApplyNow } from '@/features/auth/pages/ApplyNow'
import { InviteCodeQuestion } from '@/features/auth/pages/InviteCodeQuestion'
import { EnterInviteCode } from '@/features/auth/pages/EnterInviteCode'
import { NeedToApplyFirst } from '@/features/auth/pages/NeedToApplyFirst'
import type { ApplyWizardProps, ApplyWizardStep } from '../types'

/**
 * Angular `RegisterComponent` runs steps 1-4 from one component via `selectedTab`,
 * so the URL never changes. This mirrors that graph:
 *
 *   haveApplied ──No──▶ appliedNo            (back ▶ haveApplied)
 *               └─Yes─▶ appliedYes ──No───▶ appliedNo
 *                                  ├─Yes──▶ addInvite  (back ▶ appliedYes)
 *                                  └─used─▶ /login  (the one real navigation)
 *
 * Note appliedYes' back goes to haveApplied, skipping appliedNo — Angular's
 * transitions are not a linear stack (register.component.html:47/83/112).
 */
export function ApplyWizard({ onInviteVerified }: ApplyWizardProps) {
  const [step, setStep] = useState<ApplyWizardStep>('haveApplied')

  if (step === 'appliedNo') {
    return <NeedToApplyFirst />
  }

  if (step === 'appliedYes') {
    return (
      <InviteCodeQuestion
        onContinue={(choice) => setStep(choice === 'no' ? 'appliedNo' : 'addInvite')}
      />
    )
  }

  if (step === 'addInvite') {
    return (
      <EnterInviteCode
        // The parent already owns invite state — mounting must not wipe it.
        clearOnMount={false}
        onVerified={onInviteVerified}
      />
    )
  }

  return (
    <ApplyNow
      onContinue={(choice) => setStep(choice === 'no' ? 'appliedNo' : 'appliedYes')}
    />
  )
}
